import type { ComponentType, ReactNode } from 'react';

import type { CaptionProps } from '../Caption/Caption';

import { SyncComboBoxBase } from './SyncComboBoxBase';
import type { ComboBoxChangeMeta } from './types/ComboBoxChangeMeta';
import type { ComboBoxOptionComponentProps } from './types/ComboBoxComponentProps';
import { ComboBoxMode } from './types/ComboBoxMode';
import type { ComboBoxOption } from './types/ComboBoxOption';
import type { MultiComboBoxValue } from './types/ComboBoxValue';

interface MultiComboBoxSharedProps<Option extends ComboBoxOption> {
    options?: Option[];
    label?: string;
    labelInfo?: string;
    placeholder?: string;
    searchPlaceholder?: string;
    caption?: ReactNode;
    CaptionIcon?: CaptionProps['Icon'];
    disabled?: boolean;
    positive?: boolean;
    negative?: boolean;
    markAsRequired?: boolean;
    markAsOptional?: boolean;
    description?: string;
    isClearable?: boolean;
    isSearchable?: boolean;
    minWidth?: string;
    dropdownWidth?: string;
    dropdownMaxHeight?: string;
    noOptionsMessage?: string;
    onBlur?: () => void;
    clearSearchOnSelect?: boolean;
    OptionSeparatorComponent?: ComponentType<ComboBoxOptionComponentProps<Option>>;
}

interface MultiComboBoxSelectionProps<Option extends ComboBoxOption> {
    value?: MultiComboBoxValue<Option>;
    onChange?: (value: MultiComboBoxValue<Option>, meta: ComboBoxChangeMeta<Option>) => void;
}

export type MultiComboBoxProps<Option extends ComboBoxOption = ComboBoxOption> = MultiComboBoxSharedProps<Option> &
    MultiComboBoxSelectionProps<Option>;

export function MultiComboBox<Option extends ComboBoxOption = ComboBoxOption>({
    value = [],
    onChange,
    isClearable = true,
    clearSearchOnSelect = true,
    ...props
}: MultiComboBoxProps<Option>) {
    return (
        <SyncComboBoxBase<Option>
            {...props}
            mode={ComboBoxMode.Multi}
            value={value}
            isClearable={isClearable}
            clearSearchOnSelect={clearSearchOnSelect}
            onSelectionChange={(nextSelectedOptions, meta) => {
                onChange?.(
                    nextSelectedOptions.map((option) => option.value),
                    meta
                );
            }}
        />
    );
}
